import { useState, useEffect } from "react";
import { X, Info } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIM_META = {
  care: { color: "#34d399", formula: "(care_apps / total_apps) × 100, normalized so 25% = 100", perfect: "25% of all AI apps are care-oriented" },
  diversity: { color: "#ec4899", formula: "(female_only + mixed) / known_founders × 100, normalized so 50% = 100", perfect: "50% non-male-only founding teams" },
  governance: { color: "#8b5cf6", formula: "(system_aware + participatory) / total_apps × 100, normalized so 75% = 100", perfect: "75% of apps have ethical governance framing" },
  geographic: { color: "#3b82f6", formula: "100 - (Gini coefficient × 100), normalized so Gini 0.30 = 100", perfect: "Gini < 0.30 (innovation distributed equitably across countries)" },
  extraction: { color: "#f59e0b", formula: "Inverse: lower extraction % = higher score, normalized so <2% = 100", perfect: "Under 2% of apps are extraction-oriented" },
  inclusion: { color: "#06b6d4", formula: "(lgbtq_founded + lgbtq_focus + female_founded) / total × 100, normalized so 15% = 100", perfect: "15% of ecosystem serves underrepresented communities" },
  agency: { color: "#f97316", formula: "Apps matching user-control keywords / total × 100, normalized so 40% = 100", perfect: "40% of apps offer explicit user control and transparency" },
};

const SOURCES = {
  care: "UN SDG 3 (Good Health & Well-Being)",
  diversity: "UN Women / World Economic Forum Gender Parity",
  governance: "OECD AI Principles (2019)",
  geographic: "UNCTAD Technology Concentration Index",
  extraction: "EU AI Act high-risk thresholds",
  inclusion: "WHO / UNDP Inclusion Frameworks",
  agency: "IEEE Ethically Aligned Design (EAD)",
};

export const AICCIMethodologyModal = ({ onClose, entityType }) => {
  const [aicci, setAicci] = useState(null);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    const url = entityType ? `${API}/aicci?entity_type=${entityType}` : `${API}/aicci`;
    axios.get(url).then(r => setAicci(r.data)).catch(() => {});
    axios.get(`${API}/validation/latest`).then(r => setValidation(r.data)).catch(() => {});
  }, [entityType]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-bold">How We Measure the AICCI</h2>
            <p className="text-xs text-muted-foreground mt-0.5">AI Civilization Conscious Innovation Index{entityType ? ` — ${entityType === "application" ? "AI Apps" : entityType === "device" ? "Physical AI" : entityType === "company" ? "Companies" : entityType === "research" ? "Research" : entityType}` : " — Global"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Overview */}
          <div>
            <p className="text-sm text-slate-300 leading-relaxed">
              The AICCI (AI Civilization Conscious Innovation Index) is a composite score from <strong>0 to 100</strong> measuring how consciously humanity is building AI.
              It combines 7 dimensions, each normalized against established international benchmarks. The score updates live from {aicci?.total_apps?.toLocaleString() || "66,000+"} AI {entityType === "device" ? "devices" : entityType === "company" ? "companies" : entityType === "research" ? "research papers" : "entities"}.
            </p>
          </div>

          {/* Current score */}
          {aicci && (
            <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.15)" }}>
              <span className="text-3xl font-black" style={{ color: aicci.score >= 60 ? "#34d399" : aicci.score >= 40 ? "#f59e0b" : "#f87171" }}>{aicci.score}</span>
              <div>
                <p className="text-xs font-bold text-violet-400 uppercase tracking-wider">Current Score</p>
                <p className="text-xs text-slate-400">{aicci.interpretation}</p>
              </div>
            </div>
          )}

          {/* 7 Dimensions */}
          <div>
            <h3 className="text-sm font-bold mb-3">7 Dimensions</h3>
            <div className="space-y-4">
              {Object.entries(DIM_META).map(([key, meta]) => {
                const dim = aicci?.dimensions?.[key];
                return (
                  <div key={key} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                        <span className="text-sm font-semibold text-slate-200">{dim?.label || key}</span>
                        <span className="text-xs text-muted-foreground">({(dim?.weight ? (dim.weight * 100).toFixed(0) : "?") + "% weight"})</span>
                      </div>
                      {dim && <span className="text-sm font-bold" style={{ color: meta.color }}>{dim.score}/100</span>}
                    </div>
                    <div className="space-y-1.5 text-[11px] text-slate-400">
                      <p><span className="text-slate-400 font-medium">Formula:</span> {meta.formula}</p>
                      <p><span className="text-slate-400 font-medium">Perfect score:</span> {meta.perfect}</p>
                      <p><span className="text-slate-400 font-medium">Precedent:</span> <span className="text-blue-400">{SOURCES[key]}</span></p>
                      {dim && <p><span className="text-slate-400 font-medium">Current raw value:</span> {dim.raw}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Composite formula */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <h3 className="text-sm font-bold mb-2">Composite Score</h3>
            <p className="text-[11px] text-slate-400 font-mono leading-relaxed">
              AICCI = (Care × 0.22) + (Diversity × 0.18) + (Governance × 0.17) + (Geographic × 0.13) + (Agency × 0.12) + (Inclusion × 0.10) + (Extraction × 0.08)
            </p>
            <p className="text-[11px] text-slate-400 mt-2">Each dimension score is normalized to 0-100 against its benchmark. The composite is a weighted sum.</p>
          </div>

          {/* Inter-rater validation */}
          {validation?.summary && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
              <h3 className="text-sm font-bold mb-2">Classification Validation</h3>
              <p className="text-[11px] text-slate-400 leading-relaxed mb-3">
                To validate our LLM-based classification, we run a sample of apps through 3 independent LLMs and compare their consensus with our labels.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-lg font-bold text-emerald-400">{validation.summary.match_rate}%</p>
                  <p className="text-[10px] text-slate-400">Labels match consensus</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-lg font-bold text-blue-400">{validation.summary.high_agreement_pct}%</p>
                  <p className="text-[10px] text-slate-400">High inter-rater agreement</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-white/[0.03]">
                  <p className="text-lg font-bold text-violet-400">{validation.summary.sample_size}</p>
                  <p className="text-[10px] text-slate-400">Apps validated</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-2">Models: {validation.summary.models_used?.join(", ")}</p>
            </div>
          )}

          {/* Limitations */}
          <div className="bg-amber-500/[0.04] border border-amber-500/10 rounded-xl p-4">
            <h3 className="text-sm font-bold text-amber-400 mb-2">Limitations & Bias</h3>
            <ul className="space-y-1.5 text-[11px] text-slate-400">
              <li>Dataset over-represents English-language apps and apps from major platforms (App Store, Product Hunt, HuggingFace)</li>
              <li>~23% of apps have unknown founder composition (primarily app store listings with no public data)</li>
              <li>Classification is probabilistic, based on LLM analysis of name, tagline, and description</li>
              <li>"Care" vs "Utility" framing may reflect Western-centric values</li>
              <li>Human Agency is estimated via keyword matching, not app-level audit</li>
              <li>Geographic Spread penalizes concentrated ecosystems, which may reflect market reality rather than inequity</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border text-[10px] text-slate-500">
          <span>{typeof window !== "undefined" ? window.location.origin.replace("https://", "") : ""}</span>
          <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
        </div>
      </div>
    </div>
  );
};

export const AICCIMethodologyLink = ({ className = "", entityType }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className={`text-xs text-violet-400 hover:text-violet-300 font-medium inline-flex items-center gap-1 ${className}`} data-testid="aicci-methodology-link">
        <Info className="w-3 h-3" />How we measure →
      </button>
      {open && <AICCIMethodologyModal onClose={() => setOpen(false)} entityType={entityType} />}
    </>
  );
};

export default AICCIMethodologyModal;
